import * as React from "react";
import {
  IModalManagerContext,
  useModalManagerContext,
} from "./ModalManager.context";
import { isIFrame } from "src/helpers";
import styled, { css } from "styled-components";
import { Cross } from "@asphalt-react/iconpack";
import IconTuner from "@asphalt-react/icontuner";
import { MediaQueries } from "src/constants";
import { useWindow } from "src/hooks";
import { animated, useSpring } from "react-spring";
import { Text } from "../Text";

type IModalProps = React.HTMLProps<HTMLDivElement> & {
  height?: number;
  fitWidth?: boolean;
  slim?: boolean;
  wrapContent?: boolean;
  fitHeight?: boolean;
  maxWidth?: string;
  maxHeight?: string;
};

const ModalHeaderEl = styled.div<Partial<IModalManagerContext>>`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  padding: ${(props) => props.theme.dimension.spacing.l}px;

  ${(p) =>
    p.modalSize === "s" &&
    css`
      margin-bottom: 10px;
    `}

  ${(p) =>
    p.modalSize === "m" &&
    css`
      margin-bottom: 0px;
    `}
`;

const ModalHeaderTitleEl = styled.div`
  display: flex;
  flex: 1;
  font-size: ${(p) => p.theme.text.size.large};
  font-weight: bold;
`;

const ModalHeaderActionEl = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.text.soft};
  cursor: pointer;
  display: flex;
  font-family: ${(props) => props.theme.text.fontFamily.demi};
  font-size: 14px;
  font-weight: bold;
  outline: none;

  > p {
    margin-top: 2px;
  }
`;

export const ModalHeader = ({ children, hideCloseIcon = false }: any) => {
  const { onClose, modalSize, prompt } = useModalManagerContext();

  const showCloseIcon = prompt || !hideCloseIcon;

  return (
    <ModalHeaderEl modalSize={modalSize}>
      <ModalHeaderTitleEl>{children}</ModalHeaderTitleEl>
      {showCloseIcon && (
        <ModalHeaderActionEl onClick={onClose}>
          <IconTuner size="s">
            <Cross />
          </IconTuner>
        </ModalHeaderActionEl>
      )}
    </ModalHeaderEl>
  );
};

export const ModalTitle = ({ children }: any) => {
  return (
    <Text as="h2" fontSize="large" fontWeight="demi">
      {children}
    </Text>
  );
};

const ModalContentEl = styled.div`
  flex: auto;
  overflow-y: scroll;
  padding: ${(props) => props.theme.layout.margin.l}px;
`;

export const ModalContent = ({ children, ...props }: any) => {
  return <ModalContentEl {...props}>{children}</ModalContentEl>;
};

const ModalEl = styled(animated.div)<
  IModalManagerContext & {
    innerHeight: number;
    fitWidth?: boolean;
    fitHeight?: boolean;
    slim?: boolean;
    wrapContent?: boolean;
    maxHeight?: string;
    maxWidth?: string;
  }
>`
  background-color: white;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 64px);
  opacity: 0;
  transform: translate3d(0, 16px, 0);
  transition: all 330ms ease;

  ${(props) =>
    isIFrame() &&
    css`
      ${MediaQueries.screen.medium.minWidth} {
        position: absolute;
        top: ${props.fullScreen ? 0 : 96}px;
      }
    `}

  ${(props) =>
    props.fullScreen &&
    css`
      ${MediaQueries.screen.medium.maxWidth} {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
    `}

  ${(props) =>
    props.showModal &&
    css`
      opacity: 1;
      transform: translate3d(0, 0, 0);
    `}

    ${MediaQueries.screen.medium.minWidth} {
    border-radius: ${(p) => p.theme.dimension.borderRadius[0]}px;
    max-height: 700px;
    width: calc(100vw - 64px);

    ${(props) => css`
      height: ${props.innerHeight - 64}px;
      max-width: ${props.fitWidth
        ? "fit-content"
        : props.slim
        ? `${props.theme.dimension.width.modalSlim}px`
        : `${props.theme.layout.width.modal}px`};
    `}

    ${(props) =>
      props.wrapContent &&
      css`
        height: auto;
      `}

    ${(props) =>
      props.modalSize === "s" &&
      css`
        border-radius: ${(p) => p.theme.dimension.borderRadius[1]}px;
        max-width: 320px;
        height: auto;
      `}

      ${(p) =>
      p.modalSize === "m" &&
      css`
        border-radius: ${(p) => p.theme.dimension.borderRadius[1]}px;
        max-width: 480px;
        height: auto;
      `}

      ${(props) => css`
      ${props.fitHeight &&
      `
      height: ${
        props.fitHeight ? "min-content" : `${props.innerHeight - 64}px`
      };
      `}

      ${props.fitWidth &&
      `
      max-width: ${
        props.fitWidth
          ? "fit-content"
          : props.slim
          ? `${props.theme.dimension.width.modalSlim}px`
          : `${props.theme.layout.width.modal}px`
      };
      `}
    `}

    ${(props) =>
      props.maxWidth &&
      css`
        width: 100%;
        max-width: ${props.maxWidth};
      `}

    ${(props) =>
      props.maxHeight &&
      css`
        height: 100%;
        max-height: ${props.maxHeight};
      `}
  }

  ${(props) =>
    props.modalSize === "s" &&
    css`
      padding: 12px;
    `}

  ${(props) =>
    props.scrollable &&
    css`
      overflow-y: scroll;
    `}
`;

export const Modal = ({
  children,
  height,
  fitWidth,
  fitHeight,
  slim,
  style,
  wrapContent,
  maxWidth,
  maxHeight,
}: IModalProps) => {
  const { innerHeight } = useWindow();
  const modalContext = useModalManagerContext();

  const animatedProps = useSpring({
    config: { duration: 60 },
    opacity: 1,
    transform: "translate3d(0, 0px, 0)",
    from: {
      opacity: 0,
      transform: "translate3d(0, 32px, 0)",
    },
  });

  return (
    <ModalEl
      {...modalContext}
      style={{ ...style, ...animatedProps }}
      innerHeight={height || innerHeight}
      fitWidth={fitWidth}
      fitHeight={fitHeight}
      slim={slim}
      wrapContent={wrapContent}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
    >
      {children}
    </ModalEl>
  );
};

const ModalFooterEl = styled.div<{ alignLeft?: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.alignLeft ? "flex-start" : "flex-end")};
  margin-top: ${(p) => p.theme.layout.margin.s}px;
  padding: ${(props) => props.theme.layout.margin.l}px;

  ${MediaQueries.screen.medium.maxWidth} {
    > button,
    > div {
      flex: auto;
    }
  }
`;

interface ModalFooterProps {
  children: React.ReactNode;
  alignLeft?: boolean;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  ...props
}) => {
  return <ModalFooterEl {...props}>{children}</ModalFooterEl>;
};
